import type { SupportedLanguage } from '@/i18n/constants';

/**
 * Maps i18n locale codes to day.js locale IDs.
 *
 * day.js locales use lowercase, hyphen-separated identifiers (e.g. `fr-ca`)
 * whereas the i18n layer uses underscore-separated codes that may include a
 * script subtag (e.g. `fr_CA`, `sr_Latn`). Passing the i18n code directly to
 * `dayjs.locale()` silently keeps the previously active dayjs locale, so the
 * conversion must happen at every call site that wires i18n into dayjs.
 *
 * Only locales whose dayjs ID differs from `lang.toLowerCase()` need an entry:
 *   - `en_US` → `en`      (no `en-us` import; fall back to base English)
 *   - `fr_CA` → `fr-ca`   (Canadian French has its own dayjs locale)
 *   - `nl_NL` → `nl`      (no region-specific Dutch in dayjs)
 *   - `sr_Latn` → `sr`    (no script-tagged Serbian in dayjs)
 *
 * The typed key constraint (`SupportedLanguage`) means adding a new entry to
 * `SUPPORTED_LANGUAGES` in `src/i18n/constants.ts` will surface here at compile
 * time if it needs an override. Plain short codes (de, fr, it, …) pass through
 * via the lowercase fallback and do not need to be listed.
 *
 * Must stay in sync with the static dayjs locale imports in
 * src/plugins/dayjs/index.ts.
 */
const DAYJS_LOCALE_OVERRIDES: Partial<Record<SupportedLanguage, string>> = {
  en_US: 'en',
  fr_CA: 'fr-ca',
  nl_NL: 'nl',
  sr_Latn: 'sr'
};

/**
 * Converts an i18n locale code to the corresponding day.js locale ID.
 *
 * @param lang - The i18n locale code (e.g. 'fr_CA', 'de', 'sr_Latn').
 * @returns The day.js locale ID safe to pass to `dayjs.locale()` and
 *   `dayjs.updateLocale()`.
 */
export const toDayjsLocale = (lang: string): string =>
  DAYJS_LOCALE_OVERRIDES[lang as SupportedLanguage] ?? lang.toLowerCase();
